import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";
import Dropzone from "react-dropzone";
import { RiDeleteBin6Line } from "react-icons/ri";
import { SketchPicker } from 'react-color';
import { Staticbrands } from "../brands";

const EcommerceProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    discount: 0,
    category: "",
    stock: "",
    ageGroup: "",
    brand: "",
    images: [],
    colors: [],
    volumetricWeight: "",
    hsn: "",
    tax: ""
  });

  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Product Details", link: "#" },
  ]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    fetchProduct();
    fetchCategories();
  }, [id]);


  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/brands");
      setBrands(response.data.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    } 
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`api/v1/product/${id}`);
      setProduct(response.data.product);
      setFormData(response.data.product);
    } catch (error) {
      console.error("Error fetching product:", error);
      alert("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("api/v1/categories");
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadImageToOm = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      
      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const handleImageDrop = async (acceptedFiles) => {
    setUploadingImage(true);
    try {
      const uploadPromises = acceptedFiles.map((file) => uploadImageToOm(file));
      const uploadedUrls = await Promise.all(uploadPromises);

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
      }));
    } catch (error) {
      alert("Failed to upload images. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.images.length === 0) {
      alert("Please upload at least one product image");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(`api/v1/admin/product/${id}`, formData);
      fetchProduct();
      toggleEditModal();
      alert("Product updated successfully!");
    } catch (error) {
      alert(error.response?.data?.message || "Error updating product");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const getDiscountedPrice = (price, discount) => {
    const discountedPrice = price - (price * discount) / 100;
    return formatPrice(discountedPrice);
  };

  const ageGroups = [
    "0-2 years",
    "2-4 years",
    "4-6 years",
    "6-8 years",
    "8-10 years",
    "10+ years",
  ];

  const handleColorChange = (color) => {
    setCurrentColor(color.hex);
  };

  const addColor = () => {
    setFormData(prev => ({
      ...prev,
      colors: [...prev.colors, currentColor]
    }));
    setShowColorPicker(false);
    setCurrentColor('#000000');
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <h4>Product not found</h4>
            <Link to="/ecommerce-products">
              <Button color="primary" className="mt-2">
                Back to Products
              </Button>
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Product Details"
          breadcrumbItems={breadcrumbItems}
        />

        <Row>
          <Col lg={6}>
            <Card>
              <CardBody>
                <div className="product-images">
                  <div className="main-image mb-3">
                    <img
                      src={product.images[selectedImage].startsWith("https") ? product.images[selectedImage] : `${process.env.REACT_APP_BASEURL}${product.images[selectedImage]}`}
                      alt={product.name}
                      className="img-fluid rounded-xl"
                      style={{
                        maxHeight: "400px",
                        width: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <div className="image-thumbnails d-flex gap-2">
                    {product.images.map((img, index) => (
                      <img
                        key={index}
                        src={img.startsWith("https") ? img : `${process.env.REACT_APP_BASEURL}${img}`}
                        alt={`${product.name} ${index + 1}`}
                        className={`rounded cursor-pointer ${
                          selectedImage === index ? "border border-primary" : ""
                        }`}
                        style={{
                          height: "80px",
                          width: "80px",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedImage(index)}
                      />
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between text-center align-items-start mb-4">
                  <div>
                    <h4 className="">{product.name}</h4>
                    {product.brand && (
                      <Badge color="info" className="me-2">
                        {Staticbrands.find((brand) => brand._id === product.brand)?.title || "No Brand"}
                      </Badge>
                    )}
                    <Badge color={product.stock > 0 ? "success" : "danger"}>
                      {product.stock > 0
                        ? `In Stock (${product.stock})`
                        : "Out of Stock"}
                    </Badge>
                  </div>
                  <Button color="primary" onClick={toggleEditModal}>
                    Edit Product
                  </Button>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Price:</h5>
                  <div className="d-flex align-items-center">
                    <h4 className="mb-0 me-2">
                      {getDiscountedPrice(product.price, product.discount)}
                    </h4>
                    {product.discount > 0 && (
                      <>
                        <del className="text-muted me-2">
                          {formatPrice(product.price)}
                        </del>
                        <Badge color="success">{product.discount}% off</Badge>
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-4 ">
                  <h5 className="font-size-16">Category:</h5>
                  <p>
                    {categories.find((cat) => cat._id === product.category)
                      ?.name || "Loading..."}
                  </p>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Brand:</h5>
                  <p>{brands.find((brand) => brand._id === product.brand)?.title || "No Brand"}</p>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Age Group:</h5>
                  <p>{product.ageGroup}</p>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Available Colors:</h5>
                  <div className="d-flex gap-3 flex-wrap">
                    {product.colors?.map((color, index) => (
                      <div
                        key={index}
                        className="color-item text-center"
                        style={{
                          position: 'relative',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <div
                          className="color-circle"
                          style={{
                            backgroundColor: color,
                            width: "45px",
                            height: "45px",
                            borderRadius: "50%",
                            cursor: "pointer",
                            border: "2px solid #fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            transition: "all 0.3s ease",
                            margin: "0 auto",
                            position: "relative",
                            zIndex: "1",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.15)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                          }}
                        >
                          <div
                            className="color-reflection"
                            style={{
                              position: "absolute",
                              top: "3px",
                              left: "3px",
                              width: "15px",
                              height: "15px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)",
                              opacity: "0.7"
                            }}
                          />
                        </div>
                        <div
                          className="color-code"
                          style={{
                            fontSize: "0.75rem",
                            color: "#6c757d",
                            marginTop: "8px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: "#f8f9fa",
                            display: "inline-block",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                          }}
                        >
                          {color.toUpperCase()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Description:</h5>
                  <p className="text-muted">{product.description}</p>
                </div>

                <div className="mb-4">
                  <h5 className="font-size-16">Additional Information:</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <tbody>
                        <tr>
                          <th scope="row" style={{ width: "200px" }}>
                            Stock
                          </th>
                          <td>{product.stock} units</td>
                        </tr>
                        <tr>
                          <th scope="row">Ratings</th>
                          <td>
                            {product.ratings} / 5 ({product.numOfReviews}{" "}
                            reviews)
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Created At</th>
                          <td>
                            {new Date(product.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                        {product?.volumetricWeight!==null  && (
                          <tr>
                            <th scope="row">Volumetric Weight</th>
                            <td>{product.volumetricWeight}</td>
                          </tr>
                        )}
                        {product.hsn && (
                          <tr>
                            <th scope="row">HSN Code</th>
                            <td>{product.hsn}</td>
                          </tr>
                        )}
                        {product.tax && (
                          <tr>
                            <th scope="row">Tax</th>
                            <td>{product.tax}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Modal */}
        <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
          <ModalHeader toggle={toggleEditModal}>Edit Product</ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="name">Product Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label for="price">Price *</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="discount">Discount (%)</Label>
                    <Input
                      id="discount"
                      name="discount"
                      type="number"
                      value={formData.discount}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="stock">Stock *</Label>
                    <Input
                      id="stock"
                      name="stock"
                      type="number"
                      value={formData.stock}
                      onChange={handleInputChange}
                      required
                      min="0"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label for="brand">Brand</Label>
                    <select className="form-control" id="brand" name="brand" value={formData.brand} onChange={handleInputChange} required>
                      <option value="">Select Brand</option>
                      {brands.map((brand) => (
                        <option key={brand._id} value={brand._id}>
                          {brand.title}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="category">Category *</Label>
                    <Input
                      type="select"
                      name="category"
                      id="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="ageGroup">Age Group *</Label>
                    <Input
                      type="select"
                      name="ageGroup"
                      id="ageGroup"
                      value={formData.ageGroup}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Age Group</option>
                      {ageGroups.map((age, index) => (
                        <option key={index} value={age}>
                          {age}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label for="volumetricWeight">Volumetric Weight (kg)</Label>
                    <Input
                      id="volumetricWeight"
                      name="volumetricWeight"
                      type="number"
                      step="0.1"
                      value={formData.volumetricWeight}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="hsn">HSN Code</Label>
                    <Input
                      id="hsn"
                      name="hsn"
                      type="text"
                      value={formData.hsn}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="tax">Tax (%)</Label>
                    <Input
                      id="tax"
                      name="tax"
                      type="number"
                      value={formData.tax}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="colors">Colors</Label>
                <div className="d-flex gap-3 flex-wrap align-items-center">
                  {formData.colors?.map((color, index) => (
                    <div 
                      key={index} 
                      className="d-flex align-items-center border rounded p-2"
                      style={{ backgroundColor: color, minWidth: '100px' }}
                    >
                      <span className="me-2" style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }}>
                        {color}
                      </span>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 ms-auto"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            colors: prev.colors.filter((_, i) => i !== index)
                          }));
                        }}
                      >
                        <RiDeleteBin6Line style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }} />
                      </Button>
                    </div>
                  ))}
                  <div className="mt-2">
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    >
                      {showColorPicker ? 'Close Color Picker' : 'Add Color'}
                    </Button>
                    {showColorPicker && (
                      <div className="mt-2">
                        <SketchPicker
                          color={currentColor}
                          onChange={handleColorChange}
                        />
                        <Button
                          color="primary"
                          size="sm"
                          className="mt-2"
                          onClick={addColor}
                        >
                          Add This Color
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Product Images *</Label>
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    {formData.images.map((img, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={img.startsWith("https") ? img : `${process.env.REACT_APP_BASEURL}${img}`}
                          alt={`Product ${index + 1}`}
                          style={{
                            height: "80px",
                            width: "80px",
                            objectFit: "cover",
                          }}
                          className="rounded"
                        />
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0"
                          onClick={() => removeImage(index)}
                        >
                          <RiDeleteBin6Line size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <Dropzone
                  onDrop={handleImageDrop}
                  accept={{
                    "image/*": [".png", ".jpg", ".jpeg", ".gif"],
                  }}
                  multiple
                  disabled={uploadingImage}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      className="dropzone border rounded p-3 text-center"
                      {...getRootProps()}
                    >
                      <input {...getInputProps()} />
                      <div className="mb-3">
                        <i className="display-4 text-muted ri-upload-cloud-2-line" />
                      </div>
                      <h4>
                        {uploadingImage
                          ? "Uploading images..."
                          : "Drop files here or click to upload more images"}
                      </h4>
                    </div>
                  )}
                </Dropzone>
              </FormGroup>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  color="light"
                  onClick={toggleEditModal}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loading || uploadingImage}
                >
                  {loading ? <Spinner size="sm" /> : "Update Product"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default EcommerceProductDetail;
